import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Users, FileText, CalendarCheck, ShieldCheck, DollarSign,
  TrendingUp, TrendingDown, AlertTriangle, X, ChevronRight,
  CheckCircle2, Clock, UserPlus, ArrowRight,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_STYLES = {
  red: { dot: 'bg-red-500', bg: 'hover:bg-red-50' },
  amber: { dot: 'bg-amber-500', bg: 'hover:bg-amber-50' },
  purple: { dot: 'bg-purple-500', bg: 'hover:bg-purple-50' },
};

const AVATAR_COLORS = [
  'bg-blue-500', 'bg-emerald-500', 'bg-amber-500', 'bg-purple-500',
  'bg-rose-500', 'bg-cyan-500', 'bg-indigo-500', 'bg-orange-500',
];

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

export default function AdminDashboard() {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [agents, setAgents] = useState([]);
  const [attention, setAttention] = useState([]);
  const [pipeline, setPipeline] = useState([]);
  const [alertDismissed, setAlertDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [dashRes, lbRes, attRes, pipRes] = await Promise.all([
          axios.get(`${API_URL}/api/admin/dashboard`, getAuthHeaders()),
          axios.get(`${API_URL}/api/admin/leaderboard`, getAuthHeaders()),
          axios.get(`${API_URL}/api/admin/attention`, getAuthHeaders()),
          axios.get(`${API_URL}/api/admin/pipeline-snapshot`, getAuthHeaders()),
        ]);
        setStats(dashRes.data);
        setAgents(lbRes.data.agents || []);
        setAttention(attRes.data.items || []);
        setPipeline(pipRes.data.stages || []);
      } catch (e) {
        console.error('Admin dashboard load error:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64 text-slate-400">Loading...</div>;

  const maxLeads = Math.max(...agents.map(a => a.month_leads), 1);
  const maxPipeline = Math.max(...pipeline.map(s => s.count), 1);
  const today = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#111]" data-testid="admin-dashboard-title">Overview</h1>
          <p className="text-sm text-slate-500">Agent referral activity &middot; {today}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="text-xs gap-1.5" data-testid="export-report-btn">
            <FileText className="w-3.5 h-3.5" /> Export report
          </Button>
          <Button size="sm" className="text-xs gap-1.5 bg-[#111] hover:bg-[#333]" onClick={() => navigate('/users')} data-testid="invite-agent-btn">
            <UserPlus className="w-3.5 h-3.5" /> Invite agent
          </Button>
        </div>
      </div>

      {/* Alert Banner */}
      {stats?.show_alert && !alertDismissed && (
        <div className="flex items-center gap-3 bg-[#fef3c7] border border-[#fde68a] rounded-[10px] px-4 py-3" data-testid="alert-banner">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
          <p className="text-sm text-amber-800 flex-1">Lead activity dropped significantly this month. Review agent engagement or check for onboarding issues.</p>
          <button onClick={() => navigate('/leads')} className="text-xs font-semibold text-amber-700 hover:text-amber-900 whitespace-nowrap">View details <ArrowRight className="w-3 h-3 inline" /></button>
          <button onClick={() => setAlertDismissed(true)} className="p-0.5 hover:bg-amber-200 rounded"><X className="w-4 h-4 text-amber-600" /></button>
        </div>
      )}

      {/* Stat Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {[
          { label: 'Active agents', value: stats?.active_agents ?? 0, icon: Users, color: 'text-blue-600 bg-blue-50' },
          { label: 'Total leads', value: stats?.total_leads ?? 0, icon: FileText, color: 'text-slate-600 bg-slate-50', trend: stats?.lead_trend },
          { label: 'Demos booked', value: stats?.demos_booked ?? 0, icon: CalendarCheck, color: 'text-green-600 bg-green-50' },
          { label: 'Pending verification', value: stats?.pending_verification ?? 0, icon: ShieldCheck, color: 'text-amber-600 bg-amber-50', highlight: stats?.pending_verification > 0 },
          { label: 'Commission paid', value: `$${(stats?.commission_paid ?? 0).toLocaleString()}`, icon: DollarSign, color: 'text-emerald-600 bg-emerald-50' },
        ].map((s) => (
          <Card key={s.label} className={`border ${s.highlight ? 'border-[#fde68a]' : 'border-[#ebebeb]'} shadow-none rounded-[10px]`} data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <CardContent className="px-4 py-3.5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] text-slate-500 font-medium">{s.label}</span>
                <div className={`p-1.5 rounded-lg ${s.color}`}><s.icon className="w-3.5 h-3.5" /></div>
              </div>
              <p className="text-[22px] font-bold text-[#111] leading-none">{s.value}</p>
              {s.trend !== undefined && s.trend !== 0 && (
                <div className={`flex items-center gap-1 mt-1.5 text-[10px] font-medium ${s.trend > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                  {s.trend > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  {s.trend > 0 ? '+' : ''}{s.trend}% vs last month
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Two Column Layout */}
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Leaderboard */}
        <div className="flex-1 min-w-0">
          <Card className="border border-[#ebebeb] shadow-none rounded-[10px]" data-testid="agent-leaderboard">
            <CardContent className="p-0">
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
                <div>
                  <h2 className="text-sm font-semibold text-[#111]">Agent leaderboard</h2>
                  <p className="text-[11px] text-slate-400 mt-0.5">Leads submitted and conversion rate per agent</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px] py-0.5 px-2">This month</Badge>
                  <button onClick={() => navigate('/admin/agents')} className="text-[11px] font-medium text-[#0071E3] hover:underline">View all <ArrowRight className="w-3 h-3 inline" /></button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="text-left px-5 py-2.5 text-[10px] text-slate-400 font-medium w-8">#</th>
                      <th className="text-left py-2.5 text-[10px] text-slate-400 font-medium">Agent</th>
                      <th className="text-center py-2.5 text-[10px] text-slate-400 font-medium w-16">Leads</th>
                      <th className="text-center py-2.5 text-[10px] text-slate-400 font-medium w-16">Demos</th>
                      <th className="text-center py-2.5 text-[10px] text-slate-400 font-medium w-16">Conv.</th>
                      <th className="py-2.5 text-[10px] text-slate-400 font-medium w-32 hidden md:table-cell">Pipeline</th>
                      <th className="text-right px-5 py-2.5 text-[10px] text-slate-400 font-medium w-20">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agents.map((a, idx) => (
                      <tr key={a.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors" data-testid={`leaderboard-row-${idx}`}>
                        <td className="px-5 py-3 text-slate-400">{idx + 1}</td>
                        <td className="py-3">
                          <div className="flex items-center gap-2.5">
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0 ${AVATAR_COLORS[idx % AVATAR_COLORS.length]}`}>
                              {getInitials(a.name)}
                            </div>
                            <div className="min-w-0">
                              <p className="text-xs font-semibold text-[#111] truncate">{a.name}</p>
                              <p className="text-[10px] text-slate-400 truncate">{a.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="text-center py-3 font-bold text-[#111]">{a.month_leads}</td>
                        <td className="text-center py-3 text-slate-600">{a.demos}</td>
                        <td className="text-center py-3">
                          <span className={`font-semibold ${a.conversion_rate > 15 ? 'text-emerald-600' : a.conversion_rate >= 5 ? 'text-amber-600' : 'text-red-500'}`}>
                            {a.conversion_rate}%
                          </span>
                        </td>
                        <td className="py-3 pr-2 hidden md:table-cell">
                          <div className="w-full bg-slate-100 rounded-full h-1.5">
                            <div className="bg-[#0071E3] h-1.5 rounded-full transition-all" style={{ width: `${Math.max((a.month_leads / maxLeads) * 100, 4)}%` }} />
                          </div>
                        </td>
                        <td className="text-right px-5 py-3">
                          <Badge className={`text-[9px] py-0 border-0 ${
                            a.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                            a.status === 'unverified' ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {a.status === 'active' ? 'Active' : a.status === 'unverified' ? 'Unverified' : 'Inactive'}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                    {agents.length === 0 && (
                      <tr><td colSpan={7} className="px-5 py-8 text-center text-slate-400 text-xs">No agents found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column */}
        <div className="w-full lg:w-[360px] space-y-4 flex-shrink-0">
          {/* Attention Card */}
          <Card className="border border-[#ebebeb] shadow-none rounded-[10px]" data-testid="attention-card">
            <CardContent className="p-0">
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <h2 className="text-sm font-semibold text-[#111]">Needs your attention</h2>
                  {attention.length > 0 && <Badge className="bg-red-500 text-white border-0 text-[9px] py-0 px-1.5">{attention.length}</Badge>}
                </div>
              </div>
              <div className="divide-y divide-slate-50">
                {attention.length === 0 ? (
                  <div className="px-5 py-6 text-center">
                    <CheckCircle2 className="w-6 h-6 text-emerald-500 mx-auto mb-2" />
                    <p className="text-xs text-slate-500">All clear — no action needed</p>
                  </div>
                ) : (
                  attention.map((item, idx) => (
                    <div key={idx} className={`flex items-center gap-3 px-5 py-3 ${PRIORITY_STYLES[item.priority]?.bg || ''} transition-colors cursor-pointer`}
                      onClick={() => item.link && navigate(item.link)} data-testid={`attention-item-${idx}`}>
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${PRIORITY_STYLES[item.priority]?.dot || 'bg-slate-400'}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-[#111] truncate">{item.title}</p>
                        <p className="text-[10px] text-slate-400 truncate">{item.description}</p>
                      </div>
                      <Button size="sm" variant={item.priority === 'red' ? 'default' : 'ghost'} className={`text-[10px] h-6 px-2 ${item.priority === 'red' ? 'bg-[#111] hover:bg-[#333]' : ''}`}>
                        {item.action}
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Pipeline Snapshot */}
          <Card className="border border-[#ebebeb] shadow-none rounded-[10px]" data-testid="pipeline-snapshot">
            <CardContent className="p-0">
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
                <h2 className="text-sm font-semibold text-[#111]">Pipeline snapshot</h2>
                <button onClick={() => navigate('/leads')} className="text-[11px] font-medium text-[#0071E3] hover:underline">View full <ArrowRight className="w-3 h-3 inline" /></button>
              </div>
              <div className="px-5 py-3 space-y-2.5">
                {pipeline.map((stage) => (
                  <div key={stage.id} className="flex items-center gap-3" data-testid={`pipeline-stage-${stage.id}`}>
                    <span className="text-[11px] text-slate-500 w-24 truncate flex-shrink-0">{stage.name}</span>
                    <div className="flex-1 bg-slate-100 rounded-full h-2">
                      <div className="h-2 rounded-full transition-all" style={{
                        width: `${Math.max((stage.count / maxPipeline) * 100, stage.count > 0 ? 8 : 0)}%`,
                        backgroundColor: stage.color
                      }} />
                    </div>
                    <span className="text-xs font-bold text-[#111] w-6 text-right">{stage.count}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
